import { useQuery } from '@apollo/client'
import { Button } from 'components/atoms'
import { Icon } from 'components/atoms'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import { useState } from 'react'
import styled from 'styled-components'
import { colors } from 'theme'
import { getLastMonths, monthFormatter } from 'utils/date'

import { SCORES } from '@/graphql/queries/statistics'

import AverageScore from './statistics/AverageScore'
import Badges from './statistics/Badges'
import TotalScore from './statistics/TotalScore'

const Statistics = ({ members }) => {

  const [activeItem, setActiveItem] = useState('averageScore')
  const [selectedMember, setSelectedMember] = useState('team')
  const lastSixMonths = getLastMonths(6)
  const monthYears = lastSixMonths.map(date => format(date, 'MM/yyyy'))
  const formatedMonthYears = lastSixMonths.map(date => monthFormatter.format(date))

  const { data: scoresData } = useQuery(SCORES, {
    variables: {
      usersId: selectedMember === 'team' ? members.map(member => member.id) : [selectedMember],
      monthYears,
    },
  })

  const switchTab = item => setActiveItem(item)

  return (
    <>
      <Separator />
      <Legend>Changez la sélection pour voir les statistiques d&apos;un apprenant</Legend>
      <Title>Statistiques de
        <SelectContainer>
          <Select
            value={selectedMember}
            onChange={e => setSelectedMember(e.target.value)}
          >
            <option value="team">l&apos;équipe</option>
            {members.map(member => (
              <option key={member.id} value={member.id}>
                {member.firstname} {member.lastname}
              </option>
            ))}
          </Select>
          <StyledIcon
            fill={ colors.lightGreen }
            name="arrow_down"
            size="1em"
          />
        </SelectContainer>
      </Title>

      <NavItems>
        <Li onClick={ () => switchTab('averageScore') }>
          <Anchor $isCurrent={ activeItem === 'averageScore' }>
            Moyennes
          </Anchor>
        </Li>
        <Li onClick={ () => switchTab('totalScore') }>
          <Anchor $isCurrent={ activeItem === 'totalScore' }>
            Score cumulé
          </Anchor>
        </Li>
        {selectedMember === 'team' && (
          <Li onClick={ () => switchTab('badges') }>
            <Anchor $isCurrent={ activeItem === 'badges' }>
              Badges
            </Anchor>
          </Li>
        )}
      </NavItems>

      { activeItem === 'averageScore' && (
        <AverageScore
          selectedMember={ members.find(member => member.id === selectedMember) || 'team' }
          formatedMonthYears={ formatedMonthYears }
          scores={ scoresData?.aggregatedStatistics }
        />
      ) }
      { activeItem === 'totalScore' && (
        <TotalScore
          selectedMember={ members.find(member => member.id === selectedMember) || 'team' }
          formatedMonthYears={ formatedMonthYears }
          scores={ scoresData?.aggregatedStatistics }
        />
      ) }
      { activeItem === 'badges' && (
        <Badges members={ members } />
      ) }
    </>
  )
}

Statistics.propTypes = {
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
}

const Title = styled.h3`
  display: flex;
  align-items: center;
  margin: 0.5rem 0; 
  font-family: 'brother-1816';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: bold;
  color: ${colors.lightGreen};
`

const NavItems = styled.ul`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  padding: 0;
  margin: 0;

  @media (width <= 680px) {
    && {
      li a {
        padding: 0;
        margin-right: 1rem;
      }
    }
  }
`

const Li = styled.li`
  list-style-type: none;
`

const Anchor = styled(Button)`
  && {
    padding-left: 0;
    margin-right: 1.9rem;
    ${({ $isCurrent }) => !$isCurrent && 'opacity: 0.5'};
    font-family: 'brother-1816';
    font-style: normal;
    font-weight: 500;
    color: ${colors.midnightBlue};
  }
`

const SelectContainer = styled.div`
  display: inline-flex;
  position: relative;
  width: fit-content;
  align-items: center;
  margin-left: 0.25rem;
  border: 1px solid ${colors.lightGreen};
  border-radius: 0.5rem;
  cursor: pointer;
`

const Select = styled.select`
  field-sizing: content;
  padding: 0 calc(0.375rem + 1em + 0.5rem) 0 0.25rem;
  font-family: 'brother-1816';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: bold;
  color: ${colors.lightGreen};
  background: transparent;
  cursor: pointer;
  appearance: none;
  border: none;
  border-radius: inherit;
  outline: none;
`

const Legend = styled.aside`
  display: flex;
  flex-direction: column;
  font-family: 'brother-1816';
  font-size: 0.875rem;
  font-style: normal;
  color: ${colors.darkBlue};
`

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 0.375rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

const Separator = styled.hr`
  width: 100%;
  margin: 1.5rem 0;
  border: solid 0.5px #a2bfdd;
`

export default Statistics
